import { isEmpty } from "@swiggy-private/js-utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { isValidNumber } from "@swiggy-private/core-ui";

type TickerTimer = {
    enabled: boolean;
    minutes?: number;
    seconds?: number;
};

const NR_EVENT_TYPE = {
    UPDATED_TS_RECEIVED: "updated_ts_received",
};

export const useIPLSwiggySixesTickerTimer = (
    timestampInMs?: number,
    setCroutonTrackingInSession?: (value: boolean) => void,
) => {
    const timerRef = useRef<NodeJS.Timer>();
    const [tickerTimerInMinSec, setTickerTimerInMinSec] = useState<TickerTimer>({ enabled: false });

    const cleanupTickerTimer = useCallback(() => {
        if (!isEmpty(timerRef.current)) {
            clearInterval(timerRef.current as NodeJS.Timeout);
        }
    }, []);

    const disableTickerTimerAndComms = useCallback(() => {
        setTickerTimerInMinSec({ enabled: false });
    }, []);

    const calculateTickerInStore = useCallback(() => {
        if (isEmpty(timestampInMs) || !isValidNumber(timestampInMs)) {
            cleanupTickerTimer();
            return;
        }

        const now = Date.now();
        const diff = timestampInMs - now;
        // If time has already passed, return 0
        if (diff <= 0) {
            cleanupTickerTimer();
            setTickerTimerInMinSec({ enabled: true, minutes: 0, seconds: 0 });
            return;
        }
        // get number of minutes
        // 1 min = 60000 ms
        const minutes = Math.floor(diff / 60000);
        // get number of seconds
        // 1 sec = 1000 ms
        const seconds = Math.floor((diff % 60000) / 1000);
        setTickerTimerInMinSec({ enabled: true, minutes, seconds });
        setCroutonTrackingInSession?.(true);
    }, [cleanupTickerTimer, timestampInMs, setCroutonTrackingInSession]);

    useEffect(() => {
        timerRef.current = setInterval(calculateTickerInStore, 1000);
        calculateTickerInStore();
        return () => {
            cleanupTickerTimer();
        };
    }, [calculateTickerInStore, cleanupTickerTimer]);

    useEffect(() => {
        if (!isEmpty(timestampInMs) && isValidNumber(timestampInMs)) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.IPL_SWIGGY_SIXES_DEBUG,
                type: NR_EVENT_TYPE.UPDATED_TS_RECEIVED,
                timestampInMsec: String(timestampInMs || ""),
                clientTimeInSec: String(Math.ceil(Date.now() / 1000)),
            });
        }
    }, [timestampInMs]);

    return {
        tickerTimerInMinSec,
        cleanupTickerTimer,
        disableTickerTimerAndComms,
    };
};
